import { IIndicatorQuotation } from "api/interfaces";
import ContractModel from "api/models/ContractModel";
import ContractService from "api/services/ContractService";
import { decimal } from "utils/DecimalPrecision";
import ContractIntegrationStore from "./ContractIntegrationStore";
import ContractRowStore from "./ContractRowStore";
import ContractStore from "./ContractStore";

class Store {
  private _currentIndicatorQuotation?: IIndicatorQuotation;
  private _newIndicatorQuotation?: IIndicatorQuotation;

  async fetchAndSetPreviousQuotations(
    indicatorId?: number
  ): Promise<IIndicatorQuotation | undefined> {
    const contract = ContractStore.getCurrentContract();
    if (!contract) return;

    const quota = await this.fetchContractCurrentQuotation(
      contract,
      indicatorId
    );
    if (quota) {
      this._currentIndicatorQuotation = quota;
    }

    return quota;
  }

  private async fetchContractCurrentQuotation(
    contract: ContractModel,
    indicatorId?: number
  ): Promise<IIndicatorQuotation | undefined> {
    if (!contract) return;

    let currentIndicatorQuotation = contract.readjustmentQuotation;

    // Caso não tenha sido feito nenhum reajuste, deve se pegar a cotação no dia de criação do contrato.
    if (!currentIndicatorQuotation) {
      // Para cotação primeiro é considerado o indicador cadastrado pro contrato
      let indicator = contract.financialIndicator;
      // Caso não tenha, ele tenta obter o indicador escolhido
      if (!indicator)
        indicator = ContractIntegrationStore.findFinancialIndicator(
          indicatorId || 0
        );

      if (!indicator) return;

      const quotation =
        await ContractIntegrationStore.fetchFinancialIndicatorQuotation(
          indicator,
          contract.date
        );

      if (quotation) {
        currentIndicatorQuotation = {
          id: indicator.id,
          name: indicator.name,
          percentual: quotation.value,
          date: quotation.date,
        };
      }
    }

    return currentIndicatorQuotation;
  }

  setNewQuotationsWithManualIndicator(percentual: number, date: Date) {
    this._currentIndicatorQuotation = {
      id: -1,
      name: "manual",
      percentual: 1,
      date,
    };

    this._newIndicatorQuotation = {
      id: -1,
      name: "manual",
      percentual: 1 + percentual / 100,
      date,
    };
  }

  async fetchAndSetNewQuotationsWithERPIndicator(
    indicatorId: number,
    date: Date
  ): Promise<IIndicatorQuotation | undefined> {
    const quota = await this.fetchIndicatorForReadjustment(indicatorId, date);
    if (quota) {
      this._newIndicatorQuotation = quota;
    }

    return quota;
  }

  private async fetchIndicatorForReadjustment(
    indicatorId: number,
    date: Date
  ): Promise<IIndicatorQuotation | undefined> {
    const newIndicator =
      ContractIntegrationStore.findFinancialIndicator(indicatorId);
    if (!newIndicator) return;

    const quota =
      await ContractIntegrationStore.fetchFinancialIndicatorQuotation(
        newIndicator,
        date
      );
    if (!quota) return;

    return {
      id: newIndicator.id,
      name: newIndicator.name,
      percentual: quota.value || 0,
      date: quota.date,
    };
  }

  calculateNewContractTotals() {
    const result = {
      readjustimentPercentual: 0,
      contractTotal: 0,
      contractBalance: 0,
    };

    const contract = ContractStore.getCurrentContract();
    if (!contract) return result;

    const quota = this._currentIndicatorQuotation;
    const newQuota = this._newIndicatorQuotation;
    if (!quota || !newQuota) return result;

    const currentBalance = contract.getBalanceTotal();

    result.contractBalance = decimal(currentBalance)
      .dividedBy(quota.percentual)
      .times(newQuota.percentual)
      .toNumber();

    result.contractTotal = decimal(result.contractBalance)
      .subtract(currentBalance)
      .sum(contract.total)
      .toNumber();

    result.readjustimentPercentual = decimal(result.contractTotal)
      .dividedBy(contract.total)
      .times(100)
      .subtract(100)
      .toNumber();

    return result;
  }

  async readjustContractTotals(percentualReadjust: number) {
    const contract = ContractStore.getCurrentContract();
    if (!contract) return;

    const currentIndicatorQuotation = this._currentIndicatorQuotation;
    const newIndicatorQuotation = this._newIndicatorQuotation;

    if (!currentIndicatorQuotation || !newIndicatorQuotation) return;

    return ContractService.readjustContract(
      contract.uid,
      currentIndicatorQuotation,
      newIndicatorQuotation,
      percentualReadjust
    ).then(() => {
      ContractRowStore.refreshContractRows();
    });
  }
}

const ContractReajustmentStore = new Store();
export default ContractReajustmentStore;
